// DocuSignCallback.tsx
import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, Button, Alert, AlertTitle, CircularProgress, Snackbar } from '@mui/material';
import { formApi } from '../services/api';

const DocuSignCallback: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [signingStatus, setSigningStatus] = useState<'complete' | 'incomplete'>('incomplete');
  const [formId, setFormId] = useState<string | null>(null);
  const [duplicating, setDuplicating] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    // Check URL parameters for DocuSign return status
    const urlParams = new URLSearchParams(window.location.search);
    const event = urlParams.get('event');
    
    // Get form ID from local storage
    const currentFormId = localStorage.getItem('form_id');
    
    // Set form ID if available
    if (currentFormId) {
      setFormId(currentFormId);
    }
    
    // Check if this is a successful signing completion
    if (event === 'signing_complete') {
      setSigningStatus('complete');
    } else {
      setSigningStatus('incomplete');
    }
    
    // Simulate a small delay to avoid flashing of content
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Handler to duplicate the current form application
  const handleDuplicateApplication = async () => {
    if (!formId) return;
    
    try {
      setDuplicating(true);
      
      const newFormId = await formApi.duplicateForm(formId);
      
      setSnackbar({
        open: true,
        message: "New application created successfully!",
        severity: 'success'
      });
      
      setDuplicating(false);
      
      // Navigate to the new form
      window.location.href = `/${newFormId}`;
      
    } catch (err) {
      console.error("Failed to duplicate form:", err);
      setSnackbar({
        open: true,
        message: "Failed to create new application. Please try again.",
        severity: 'error'
      });
      
      setDuplicating(false);
    }
  };

  // Reusable component for the duplicate application section
  const DuplicateApplicationSection = () => (
    formId && (
      <Box sx={{ mt: 3, p: 3, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #2196f3' }}>
        <Typography variant="h6" color="primary" gutterBottom>
          Have multiple reinsurance companies?
        </Typography>
        <Typography paragraph>
          Click the following button to start a new application process with the same information.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Button 
            variant="contained" 
            color="secondary"
            onClick={handleDuplicateApplication}
            disabled={duplicating}
          >
            {duplicating ? "Creating..." : "Start New Application"}
          </Button>
        </Box>
      </Box>
    )
  );

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} thickness={4} />
          <Box sx={{ mt: 2 }}>Processing signature completion...</Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Logo */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <img
          src={`/${import.meta.env.VITE_LOGO_PATH}`}
          alt="Logo"
          style={{ maxWidth: '400px' }}
        />
      </Box>
      
      {signingStatus === 'complete' && (
        <>
          <Alert severity="success" sx={{ mb: 4 }}>
            <AlertTitle>Signing Complete</AlertTitle>
            All documents have been signed successfully!
          </Alert>
          
          <Box sx={{ mt: 4, p: 4, bgcolor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Thank You
            </Typography>
            
            <Typography paragraph>
              Your signature has been recorded. Your application is now being processed. 
              You will be contacted if any additional information is needed.
            </Typography>
            
            <DuplicateApplicationSection />
            
            <Typography paragraph sx={{ mt: 2, fontWeight: 'bold', textAlign: 'center' }}>
              You can now close this window.
            </Typography>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => window.close()}
              >
                Close Window
              </Button>
            </Box>
          </Box>
        </>
      )}
      
      {signingStatus === 'incomplete' && (
        <>
          <Alert severity="warning" sx={{ mb: 4 }}>
            <AlertTitle>Signing Not Complete</AlertTitle>
            The document signing process is not yet complete.
          </Alert>
          
          <Box sx={{ mt: 4, p: 4, bgcolor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Next Steps
            </Typography>
            
            <Typography paragraph>
              You will receive your reinsurance application via email from DocuSign. Please review and sign. 
              Once executed, you will receive a finalized copy, and your application will be processed. 
              If you have any questions, please reach out to reinsurance@buckeyerisk.com or your Account Representative.
            </Typography>
            
            <DuplicateApplicationSection />
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => window.close()}
              >
                Close Window
              </Button>
            </Box>
          </Box>
        </>
      )}
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DocuSignCallback;